import React, { useContext, useMemo } from 'react';
import { Alert, Box, Button, IconButton, MenuItem, ThemeProvider, useMediaQuery } from '@mui/material';
import { Edit, Delete } from '@mui/icons-material';
import { MaterialReactTable } from 'material-react-table';
import { Context } from '../../../context/UserContext';
import { getMRTDefaultProps } from '../../../helpers/mrt/getMRTDefaultProps';
import tableThemeCustom from '../../form/tableThemeCustom';
import CustomDialog from '../../layout/CustomDialog';
import { usePermissoesModule } from './modules/usePermissoesModule';
import { useListagemModule } from './modules/useListagemModule';
import DocumentosDetailPanel from './tabs/DocumentosDetailPanel';

export default function Index() {
    const { parametro } = useContext(Context);
    const permissoes = usePermissoesModule();
    const lista = useListagemModule({ parametro, permissoes });
    const theme = tableThemeCustom();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const defaults = getMRTDefaultProps({ mrt: lista.mrt, parametro, isMobile });
    const columns = useMemo(
        () => [
            {
                id: 'eleicao',
                header: 'Cadastro de eleições',
                columns: [
                    { accessorKey: 'Empresa.nomeFantasia', header: 'Empresa' },
                    { accessorKey: 'descricao', header: 'Eleição' },
                    {
                        accessorKey: 'dataEleicao',
                        header: 'Data da eleição',
                        filterFn: 'equals',
                        columnFilterModeOptions: ['equals'],
                        muiFilterTextFieldProps: { type: 'date', InputLabelProps: { shrink: true } },
                        Cell: ({ cell }) => (cell.getValue() ? cell.getValue().split('-').reverse().join('/') : ''),
                    },
                    { accessorKey: 'descricaoMandato', header: 'Mandato' },
                    {
                        accessorKey: 'inicioMandato',
                        header: 'Início do mandato',
                        filterFn: 'equals',
                        columnFilterModeOptions: ['equals'],
                        muiFilterTextFieldProps: { type: 'date', InputLabelProps: { shrink: true } },
                        Cell: ({ cell }) => (cell.getValue() ? cell.getValue().split('-').reverse().join('/') : ''),
                    },
                    {
                        accessorKey: 'fimMandato',
                        header: 'Fim do mandato',
                        filterFn: 'equals',
                        columnFilterModeOptions: ['equals'],
                        muiFilterTextFieldProps: { type: 'date', InputLabelProps: { shrink: true } },
                        Cell: ({ cell }) => (cell.getValue() ? cell.getValue().split('-').reverse().join('/') : ''),
                    },
                ],
            },
        ],
        []
    );
    if (!permissoes.autorizado) return <Alert severity={permissoes.pronto ? 'warning' : 'info'}>{permissoes.mensagem}</Alert>;
    return (
        <ThemeProvider theme={theme}>
            <Box sx={{ width: '100%', minWidth: 0 }}>
                {lista.selecionada && (
                    <CustomDialog
                        title="Excluir eleição"
                        content="Excluir esta eleição e sua comissão?"
                        onClose={() => {
                            if (!lista.excluindo) lista.setSelecionada(null);
                        }}
                        onConfirm={lista.excluir}
                    />
                )}
                <MaterialReactTable
                    {...defaults}
                    columns={columns}
                    data={lista.dados}
                    getRowId={(r) => r.uuid}
                    enableExpanding
                    enableExpandAll={false}
                    renderDetailPanel={({ row }) => (
                        <DocumentosDetailPanel
                            key={`${row.original.uuid}:${localStorage.getItem('empresaId')}`}
                            eleicao={row.original}
                            permissoes={permissoes}
                            parametro={parametro}
                            isMobile={isMobile}
                        />
                    )}
                    muiPaginationProps={{
                        ...defaults.muiPaginationProps,
                        rowsPerPageOptions: defaults.muiPaginationProps.rowsPerPageOptions.filter((v) => v <= 200),
                    }}
                    renderTopToolbarCustomActions={() => (
                        <Button
                            variant="contained"
                            color="secondary"
                            disabled={!permissoes.incluir}
                            onClick={() => lista.abrir()}
                        >
                            Novo
                        </Button>
                    )}
                    renderRowActions={
                        isMobile
                            ? undefined
                            : ({ row }) => (
                                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                      <IconButton
                                          title="Editar"
                                          disabled={!permissoes.editar}
                                          onClick={() => lista.abrir(row.original)}
                                      >
                                          <Edit />
                                      </IconButton>
                                      <IconButton
                                          title="Excluir"
                                          color="error"
                                          disabled={!permissoes.cancelar}
                                          onClick={() => lista.setSelecionada(row.original)}
                                      >
                                          <Delete />
                                      </IconButton>
                                  </Box>
                              )
                    }
                    renderRowActionMenuItems={
                        isMobile
                            ? ({ row, closeMenu }) => [
                                  <MenuItem
                                      key="editar"
                                      disabled={!permissoes.editar}
                                      onClick={() => {
                                          closeMenu();
                                          lista.abrir(row.original);
                                      }}
                                  >
                                      Editar
                                  </MenuItem>,
                                  <MenuItem
                                      key="excluir"
                                      disabled={!permissoes.cancelar}
                                      onClick={() => {
                                          closeMenu();
                                          lista.setSelecionada(row.original);
                                      }}
                                  >
                                      Excluir
                                  </MenuItem>,
                              ]
                            : undefined
                    }
                />
            </Box>
        </ThemeProvider>
    );
}
